/**
 * useAuthorPlatforms — real-time subscription to platforms enabled for an
 * author at projects/{pid}/{authors|external_authors}/{authorId}/platforms/*.
 *
 * `basePath` switches between self-author (`authors` — default) and tracked
 * creator (`external_authors`) — same architecture, different collection.
 *
 * Phase A nesting: PlatformVoice + PostFormat + PostExamples + author_posts
 * + author_analyses all live under the platform doc. This hook surfaces
 * only the top-level platform docs; consumers fetch nested data on demand.
 */

import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { AuthorBasePath } from '../services/AuthorPlatformService';

export interface AuthorPlatformLite {
  id: string;
  displayName?: string;
  isActive?: boolean;
  promptRef?: string;
  hasAlgorithm?: boolean;
  linkPenalty?: number;
  optimalTimes?: Array<string | { day?: string | number; hour?: number; score?: number }>;
  optimalSlots?: Array<{ day?: string | number; timeLocal?: string; score?: number }>;
  maxPostLength?: number;
  optimalPostFrequency?: { min?: number; max?: number };
  /**
   * Author's URL on this specific platform (canonical Platform.profileUrl,
   * schema 1.3.1+). Used by Posts tab "Scrape more" button to know which
   * URL to re-scrape. Per-platform — LinkedIn URL ≠ Twitter URL for the
   * same author. Optional: manual-paste platforms have no URL.
   */
  profileUrl?: string;
  [key: string]: any;
}

export interface UseAuthorPlatformsReturn {
  platforms: AuthorPlatformLite[];
  loading: boolean;
  error: Error | null;
}

export function useAuthorPlatforms(
  projectId: string | null,
  authorId: string | null,
  basePath: AuthorBasePath = 'authors',
): UseAuthorPlatformsReturn {
  const [platforms, setPlatforms] = useState<AuthorPlatformLite[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(projectId && authorId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !authorId) {
      setPlatforms([]);
      setLoading(false);
      return;
    }
    const db = CoreFirebaseService.getInstance().firestore;
    const colRef = collection(
      db,
      'projects',
      projectId,
      basePath,
      authorId,
      'platforms',
    );
    setLoading(true);
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        setPlatforms(
          snap.docs.map((d) => ({ ...(d.data() as AuthorPlatformLite), id: d.id })),
        );
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId, authorId, basePath]);

  return { platforms, loading, error };
}
